/**
 * Mockdata en berekeningen voor de publieke product-demo (geen API).
 * Regels zijn bewust gedetailleerd (hoofdgroep, subgroep, library-ref, variant) om het prijsboek te illustreren.
 */

export type DemoScenarioId = 'renovatie' | 'splitsing' | 'transformatie';

export type DemoScope = 0 | 1 | 2;

export type DemoKlanttype = 'particulier' | 'zakelijk' | 'corporatie';

export interface DemoLine {
  id: string;
  /** Bouwcluster (zoals bouwcluster in de app) */
  cluster: string;
  /** Eerste laag in het prijsboek */
  hoofdgroep: string;
  /** Tweede laag: deelgebied binnen hoofdgroep */
  subgroep: string;
  /** Demo library-sleutel (illustratief, geen echte CALQ-code) */
  libraryRef: string;
  /** Gekozen catalogusvariant */
  variantLabel: string;
  /** Discipline (WTB, E, interieur, ruwbouw, …) */
  discipline: string;
  omschrijving: string;
  eenheid: string;
  aantal: number;
  /** Modelbedrag excl. btw voor deze regel vóór scope-vermenigvuldiger */
  basisSubtotaal: number;
  optional?: boolean;
}

export interface DemoScenarioMeta {
  id: DemoScenarioId;
  label: string;
  dossierNaam: string;
  projecttype: string;
  teaser: string;
}

export const SCENARIO_META: Record<DemoScenarioId, DemoScenarioMeta> = {
  renovatie: {
    id: 'renovatie',
    label: 'Renovatie woning',
    dossierNaam: 'Renovatie rijtjeshuis — Utrecht',
    projecttype: 'Renovatie / verbouw',
    teaser: 'Typisch particuliere verbouwing: keuken, badkamer, afwerkingspakketten.',
  },
  splitsing: {
    id: 'splitsing',
    label: 'Woningsplitsing',
    dossierNaam: 'Splitsing 1→2 appartementen — Haarlem',
    projecttype: 'Transformatie / splitsing',
    teaser: 'Nieuwe tussenschotten, tweede voorzieningenblok, regelgeving in de posten.',
  },
  transformatie: {
    id: 'transformatie',
    label: 'Kantoor → woningen',
    dossierNaam: 'Kantoortransformatie — Eindhoven',
    projecttype: 'Utiliteit → woningbouw',
    teaser: 'Grotere volumes, clusters per bouwdeel en hogere materiaalgraad.',
  },
};

export const SCENARIO_LINES: Record<DemoScenarioId, DemoLine[]> = {
  renovatie: [
    {
      id: 'ren-01',
      cluster: 'Keuken',
      hoofdgroep: 'Interieur',
      subgroep: 'Keuken — voorbereiding',
      libraryRef: 'DEMO-NL-INT-K-SLP-01',
      variantLabel: 'Particulier standaard',
      discipline: 'Interieur',
      omschrijving: 'Sloop en afvoer bestaand keukenblok, afsluitingen en afvoer tijdelijk afdekken',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 3_200,
    },
    {
      id: 'ren-02',
      cluster: 'Keuken',
      hoofdgroep: 'Installaties',
      subgroep: 'Warm / koud water',
      libraryRef: 'DEMO-NL-WT-K-WK-04',
      variantLabel: 'Koper / PEX, bestaande schacht',
      discipline: 'WTB',
      omschrijving: 'Hercapaciteit leidingwerk warm/koud t.b.v. nieuwe keukenindeling incl. keerkleppen',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 8_900,
    },
    {
      id: 'ren-03',
      cluster: 'Keuken',
      hoofdgroep: 'E-installatie',
      subgroep: 'Groepenkast uitbreiding',
      libraryRef: 'DEMO-NL-E-K-GRP-02',
      variantLabel: '3 fase, 2 extra groepen + aardlek',
      discipline: 'E',
      omschrijving: 'Uitbreiding meterkast en bekabeling naar keukenzone (fornuis / oven / vaatwasser)',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 6_200,
    },
    {
      id: 'ren-04',
      cluster: 'Keuken',
      hoofdgroep: 'Interieur',
      subgroep: 'Tegelwerk vloer',
      libraryRef: 'DEMO-NL-INT-K-TV-08',
      variantLabel: 'Keramisch 600×600, lijm C2',
      discipline: 'Interieur',
      omschrijving: 'Vloertegels keuken incl. egaline-onderlaag en voegwerk (excl. tegels levering klant)',
      eenheid: 'm²',
      aantal: 26,
      basisSubtotaal: 5_800,
    },
    {
      id: 'ren-05',
      cluster: 'Keuken',
      hoofdgroep: 'Interieur',
      subgroep: 'Tegelwerk wand',
      libraryRef: 'DEMO-NL-INT-K-TW-05',
      variantLabel: 'Spatscherm 2,4 m hoog',
      discipline: 'Interieur',
      omschrijving: 'Wandtegelwerk achterwand en zijvlakken werkblad, hoekprofielen RVS-look',
      eenheid: 'm²',
      aantal: 8,
      basisSubtotaal: 2_400,
    },
    {
      id: 'ren-06',
      cluster: 'Keuken',
      hoofdgroep: 'Interieur',
      subgroep: 'Montage keuken',
      libraryRef: 'DEMO-NL-INT-K-MNT-03',
      variantLabel: 'Werkblad composiet + frontmontage',
      discipline: 'Interieur',
      omschrijving: 'Montage werkblad, fronten, grepen en inbouwapparatuur (apparatuur excl., aansluitingen incl.)',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 18_500,
    },
    {
      id: 'ren-07',
      cluster: 'Badkamer',
      hoofdgroep: 'Interieur',
      subgroep: 'Sloop badkamer',
      libraryRef: 'DEMO-NL-INT-B-SLP-01',
      variantLabel: 'Particulier',
      discipline: 'Interieur',
      omschrijving: 'Gestripte sloop badkamer, afvoer afdekken, bouwplaatsinrichting klein',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 2_800,
    },
    {
      id: 'ren-08',
      cluster: 'Badkamer',
      hoofdgroep: 'Interieur',
      subgroep: 'Natte cel',
      libraryRef: 'DEMO-NL-INT-B-NC-12',
      variantLabel: 'Volledige douchezone',
      discipline: 'Interieur',
      omschrijving: 'Waterdichting vloer/wand, hoekafdichting, inspectieluik voorziening',
      eenheid: 'm²',
      aantal: 12,
      basisSubtotaal: 11_400,
    },
    {
      id: 'ren-09',
      cluster: 'Badkamer',
      hoofdgroep: 'Interieur',
      subgroep: 'Tegelwerk',
      libraryRef: 'DEMO-NL-INT-B-TW-09',
      variantLabel: 'Midden segment keramiek',
      discipline: 'Interieur',
      omschrijving: 'Tegelwerk wand en vloer badkamer, plinttegels, siliconen afwerking',
      eenheid: 'm²',
      aantal: 22,
      basisSubtotaal: 9_200,
    },
    {
      id: 'ren-10',
      cluster: 'Badkamer',
      hoofdgroep: 'Sanitair',
      subgroep: 'Inbouwpakket',
      libraryRef: 'DEMO-NL-WT-B-SAN-06',
      variantLabel: 'Inloopdouche + wandcloset + wastafelmeubel',
      discipline: 'WTB',
      omschrijving: 'Levering en montage sanitairset incl. mengkranen en sifons (excl. tegelwerk)',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 12_600,
    },
    {
      id: 'ren-11',
      cluster: 'Badkamer',
      hoofdgroep: 'Installaties',
      subgroep: 'Mechanische ventilatie',
      libraryRef: 'DEMO-NL-WT-B-MV-02',
      variantLabel: 'Aansluiting bestaande kanaal',
      discipline: 'WTB',
      omschrijving: 'Doorvoer en aansluiting MV naar badkamer, demper en rooster',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 4_200,
    },
    {
      id: 'ren-12',
      cluster: 'Algemeen',
      hoofdgroep: 'Interieur',
      subgroep: 'Houtwerk / afwerking',
      libraryRef: 'DEMO-NL-INT-AL-DR-04',
      variantLabel: 'Bestaande kozijnen',
      discipline: 'Interieur',
      omschrijving: 'Deuren woning afhangen en verstellen na vloerwerkzaamheden',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 3_600,
    },
    {
      id: 'ren-13',
      cluster: 'Algemeen',
      hoofdgroep: 'Interieur',
      subgroep: 'Afwerk hulp',
      libraryRef: 'DEMO-NL-INT-AL-PL-01',
      variantLabel: 'Standaard',
      discipline: 'Interieur',
      omschrijving: 'Plinten, afkitten vensterbanken en overgangen naar vloer',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 3_200,
    },
    {
      id: 'ren-14',
      cluster: 'Algemeen',
      hoofdgroep: 'Ruwbouw',
      subgroep: 'Ondergrond',
      libraryRef: 'DEMO-NL-RW-AL-EG-03',
      variantLabel: 'Cementgebonden egaline',
      discipline: 'Ruwbouw',
      omschrijving: 'Egalisatielaag vloer woonverdieping t.b.v. tegels (dikte indicatief)',
      eenheid: 'm²',
      aantal: 45,
      basisSubtotaal: 8_900,
    },
    {
      id: 'ren-15',
      cluster: 'Algemeen',
      hoofdgroep: 'Riolering',
      subgroep: 'Inspectie',
      libraryRef: 'DEMO-NL-RI-AL-INS-01',
      variantLabel: 'Camera + rapport',
      discipline: 'WTB',
      omschrijving: 'Inspectie bestaande riolering hoofdafvoer (camera, korte rapportage)',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 6_800,
    },
    {
      id: 'ren-16',
      cluster: 'Algemeen',
      hoofdgroep: 'Interieur',
      subgroep: 'Glas',
      libraryRef: 'DEMO-NL-INT-AL-BEG-02',
      variantLabel: 'HR++ tussen glas',
      discipline: 'Interieur',
      omschrijving: 'Vervanging beglazing keukenraam (veiligheid + warmte)',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 4_200,
    },
    {
      id: 'ren-17',
      cluster: 'Algemeen',
      hoofdgroep: 'Algemene kosten',
      subgroep: 'Project',
      libraryRef: 'DEMO-NL-AK-PRJ-MGT-01',
      variantLabel: 'Midden particulier',
      discipline: 'Organisatie',
      omschrijving: 'Werkvoorbereiding, planning en bouwbegeleiding (gebundeld urenpakket)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 12_200,
    },
    {
      id: 'ren-o1',
      cluster: 'Installatie',
      hoofdgroep: 'Installaties',
      subgroep: 'Vloerverwarming',
      libraryRef: 'DEMO-NL-WT-VV-VER-02',
      variantLabel: 'Verdeler 6 groepen + pomp',
      discipline: 'WTB',
      omschrijving: 'Verdeelstation vloerverwarming incl. afregelen en hydraulische inregeling',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 4_200,
      optional: true,
    },
    {
      id: 'ren-o2',
      cluster: 'Installatie',
      hoofdgroep: 'Installaties',
      subgroep: 'Vloerverwarming',
      libraryRef: 'DEMO-NL-WT-VV-AAN-07',
      variantLabel: 'Natte aanleg PE-RT',
      discipline: 'WTB',
      omschrijving: 'Aanleg vloerverwarmingslus woonkamer/eet (buiswerk + isolatieplaat)',
      eenheid: 'm²',
      aantal: 42,
      basisSubtotaal: 11_500,
      optional: true,
    },
    {
      id: 'ren-o3',
      cluster: 'Afwerk',
      hoofdgroep: 'Interieur',
      subgroep: 'Stucwerk',
      libraryRef: 'DEMO-NL-INT-AF-STU-04',
      variantLabel: 'Machinaal + hoekprofielen',
      discipline: 'Interieur',
      omschrijving: 'Stucwerk wanden hoofdvertrekken (excl. behang)',
      eenheid: 'm²',
      aantal: 95,
      basisSubtotaal: 22_400,
      optional: true,
    },
    {
      id: 'ren-o4',
      cluster: 'Afwerk',
      hoofdgroep: 'Interieur',
      subgroep: 'Schilderwerk',
      libraryRef: 'DEMO-NL-INT-AF-SCH-03',
      variantLabel: 'Latex 2 lagen plafonds/wanden',
      discipline: 'Interieur',
      omschrijving: 'Schilderwerk binnen na stuc (grond + 2 lagen)',
      eenheid: 'm²',
      aantal: 95,
      basisSubtotaal: 14_200,
      optional: true,
    },
  ],
  splitsing: [
    {
      id: 'spl-01',
      cluster: 'Constructie',
      hoofdgroep: 'Ruwbouw',
      subgroep: 'Tussenschotten',
      libraryRef: 'DEMO-NL-RW-C-TSW-11',
      variantLabel: 'Gipskalk 100 mm + profielen',
      discipline: 'Ruwbouw',
      omschrijving: 'Nieuwe scheidingswanden appartement B t.o.v. trappenhuis en liftkern (dragend n.v.t.)',
      eenheid: 'm²',
      aantal: 38,
      basisSubtotaal: 24_800,
    },
    {
      id: 'spl-02',
      cluster: 'Constructie',
      hoofdgroep: 'Ruwbouw',
      subgroep: 'Dragende aanpassing',
      libraryRef: 'DEMO-NL-RW-C-DRG-04',
      variantLabel: 'Constructeur gecontroleerd',
      discipline: 'Ruwbouw',
      omschrijving: 'Aanpassing liggerdoorlaat + stalen ligger inkorten (engineeringset inbegrepen)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 18_600,
    },
    {
      id: 'spl-03',
      cluster: 'Constructie',
      hoofdgroep: 'Ruwbouw',
      subgroep: 'Vloeren',
      libraryRef: 'DEMO-NL-RW-C-VL-06',
      variantLabel: 'Gelijkvloers appartement B',
      discipline: 'Ruwbouw',
      omschrijving: 'Dekvloer versterking + ontkoppelingsmat onder tegelvloer',
      eenheid: 'm²',
      aantal: 52,
      basisSubtotaal: 14_200,
    },
    {
      id: 'spl-04',
      cluster: 'Constructie',
      hoofdgroep: 'Ruwbouw',
      subgroep: 'Plafonds',
      libraryRef: 'DEMO-NL-RW-C-PLF-02',
      variantLabel: 'Brandwerend systeemplafond',
      discipline: 'Ruwbouw',
      omschrijving: 'Verlaagd plafond technische zone + inspectieluiken MV/W',
      eenheid: 'm²',
      aantal: 24,
      basisSubtotaal: 9_400,
    },
    {
      id: 'spl-05',
      cluster: 'Brand',
      hoofdgroep: 'Brandveiligheid',
      subgroep: 'Compartiment',
      libraryRef: 'DEMO-NL-BR-COMP-05',
      variantLabel: 'Schema B woning',
      discipline: 'Brand',
      omschrijving: 'Compartimentering scheiding tussen woningen + afdichtingen doorvoeren',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 16_200,
    },
    {
      id: 'spl-06',
      cluster: 'Brand',
      hoofdgroep: 'Brandveiligheid',
      subgroep: 'Deuren',
      libraryRef: 'DEMO-NL-BR-DR-03',
      variantLabel: 'EI30 + rookdicht',
      discipline: 'Brand',
      omschrijving: 'Brandwerende deuren trappenhuis + zelfsluiters en onderdorpels',
      eenheid: 'st',
      aantal: 3,
      basisSubtotaal: 8_700,
    },
    {
      id: 'spl-07',
      cluster: 'Installatie',
      hoofdgroep: 'WTB',
      subgroep: 'Water',
      libraryRef: 'DEMO-NL-WT-SPL-W-08',
      variantLabel: 'Tweede woning aansluiting',
      discipline: 'WTB',
      omschrijving: 'Hoofdleiding splitsing + watermeter appartement B + drukproef',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 12_400,
    },
    {
      id: 'spl-08',
      cluster: 'Installatie',
      hoofdgroep: 'WTB',
      subgroep: 'Riolering',
      libraryRef: 'DEMO-NL-WT-SPL-R-05',
      variantLabel: 'PVC + inspectie',
      discipline: 'WTB',
      omschrijving: 'Afvoer nieuwe badkamer/keuken B verticaal naar hoofdriool + ontstoppingpunten',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 15_800,
    },
    {
      id: 'spl-09',
      cluster: 'Installatie',
      hoofdgroep: 'E-installatie',
      subgroep: 'Groepenkast',
      libraryRef: 'DEMO-NL-E-SPL-GRP-04',
      variantLabel: '3×12A + kookgroep',
      discipline: 'E',
      omschrijving: 'Aparte groepenkast appartement B + aarding + hoofdschakelaar',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 11_200,
    },
    {
      id: 'spl-10',
      cluster: 'Installatie',
      hoofdgroep: 'E-installatie',
      subgroep: 'Bekabeling',
      libraryRef: 'DEMO-NL-E-SPL-BEK-06',
      variantLabel: 'LS ringen + data',
      discipline: 'E',
      omschrijving: 'Volledige woningbekabeling B incl. netwerk naar meterkast hoofdgebouw',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 14_600,
    },
    {
      id: 'spl-11',
      cluster: 'Installatie',
      hoofdgroep: 'WTB',
      subgroep: 'CV / warm tapwater',
      libraryRef: 'DEMO-NL-WT-SPL-CV-03',
      variantLabel: 'Aansluiting collectief',
      discipline: 'WTB',
      omschrijving: 'Aansluiting op bestaande collectieve warmtebron + afleverset appartement B',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 9_600,
    },
    {
      id: 'spl-12',
      cluster: 'Interieur',
      hoofdgroep: 'Interieur',
      subgroep: 'Stuc / afwerk',
      libraryRef: 'DEMO-NL-INT-SPL-STU-02',
      variantLabel: 'Nieuwbouwafwerk',
      discipline: 'Interieur',
      omschrijving: 'Stucwerk en schilderwerk basisafwerk appartement B (excl. keuze wandafwerking klant)',
      eenheid: 'm²',
      aantal: 118,
      basisSubtotaal: 21_200,
    },
    {
      id: 'spl-13',
      cluster: 'Algemeen',
      hoofdgroep: 'Algemene kosten',
      subgroep: 'Vergunningen',
      libraryRef: 'DEMO-NL-AK-VG-SPL-01',
      variantLabel: 'Omgevingsplan check',
      discipline: 'Organisatie',
      omschrijving: 'Vergunningstraject splitsing + melding brandveilig gebruik (bundel)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 8_900,
    },
    {
      id: 'spl-14',
      cluster: 'Algemeen',
      hoofdgroep: 'Algemene kosten',
      subgroep: 'Project',
      libraryRef: 'DEMO-NL-AK-PRJ-SPL-02',
      variantLabel: 'Projectleiding',
      discipline: 'Organisatie',
      omschrijving: 'Directievoering, planning en opleveringsdossier (incl. Foto- en meetstaat)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 19_400,
    },
    {
      id: 'spl-o1',
      cluster: 'Keuken / nat',
      hoofdgroep: 'Interieur',
      subgroep: 'Keuken compact',
      libraryRef: 'DEMO-NL-INT-SPL-K-05',
      variantLabel: '2,8 m front + apparatuur',
      discipline: 'Interieur',
      omschrijving: 'Compacte keukenopstelling appartement B incl. montage en aansluitingen',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 28_500,
      optional: true,
    },
    {
      id: 'spl-o2',
      cluster: 'Keuken / nat',
      hoofdgroep: 'Interieur',
      subgroep: 'Badkamer compact',
      libraryRef: 'DEMO-NL-INT-SPL-B-04',
      variantLabel: 'Douche + toiletcombinatie',
      discipline: 'Interieur',
      omschrijving: 'Natte cel appartement B incl. tegelwerk standaard wit grijs',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 32_700,
      optional: true,
    },
    {
      id: 'spl-o3',
      cluster: 'Brand',
      hoofdgroep: 'Brandveiligheid',
      subgroep: 'Detectie',
      libraryRef: 'DEMO-NL-BR-DET-02',
      variantLabel: 'Koppelbaar aan centrale',
      discipline: 'Brand',
      omschrijving: 'Rookmelders woning B + koppeling bestaand centraal meldsysteem',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 4_800,
      optional: true,
    },
    {
      id: 'spl-o4',
      cluster: 'Installatie',
      hoofdgroep: 'WTB',
      subgroep: 'Koeling',
      libraryRef: 'DEMO-NL-WT-SPL-KL-01',
      variantLabel: 'Split-unit 3,5 kW',
      discipline: 'WTB',
      omschrijving: 'Airco comfort koeling slaapkamer B (buitenunit balkon)',
      eenheid: 'st',
      aantal: 1,
      basisSubtotaal: 6_900,
      optional: true,
    },
    {
      id: 'spl-o5',
      cluster: 'Algemeen',
      hoofdgroep: 'Algemene kosten',
      subgroep: 'Bouwplaats',
      libraryRef: 'DEMO-NL-AK-BP-01',
      variantLabel: 'Standaard',
      discipline: 'Organisatie',
      omschrijving: 'Bouwplaatsinrichting, afzetting trappenhuis en liftbeveiliging tijdens werk',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 7_200,
      optional: true,
    },
  ],
  transformatie: [
    {
      id: 'tr-01',
      cluster: 'Sloop',
      hoofdgroep: 'Sloopwerk',
      subgroep: 'Inventarisatie',
      libraryRef: 'DEMO-NL-SL-INV-02',
      variantLabel: 'Utiliteitsgebouw',
      discipline: 'Sloop',
      omschrijving: 'Asbestverkenning + sloopplan (fase 0, rapportage)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 28_000,
    },
    {
      id: 'tr-02',
      cluster: 'Sloop',
      hoofdgroep: 'Sloopwerk',
      subgroep: 'Niet-dragend',
      libraryRef: 'DEMO-NL-SL-ND-08',
      variantLabel: 'Kantoorinternals',
      discipline: 'Sloop',
      omschrijving: 'Sloop scheidingswanden, systeemplafonds, vloerbedekking en hulpinstallaties (niet-dragend)',
      eenheid: 'm²',
      aantal: 1_240,
      basisSubtotaal: 62_000,
    },
    {
      id: 'tr-03',
      cluster: 'Sloop',
      hoofdgroep: 'Sloopwerk',
      subgroep: 'Dragend voorzichtig',
      libraryRef: 'DEMO-NL-SL-DG-03',
      variantLabel: 'Gedeeltelijke kern',
      discipline: 'Sloop',
      omschrijving: 'Selectieve sloop vloervelden en trapopgangen onder steun (tijdelijke stut)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 96_000,
    },
    {
      id: 'tr-04',
      cluster: 'Ruwbouw',
      hoofdgroep: 'Ruwbouw',
      subgroep: 'Betonstructuren',
      libraryRef: 'DEMO-NL-RW-BS-REH-05',
      variantLabel: 'Herstel en versterking',
      discipline: 'Ruwbouw',
      omschrijving: 'Carbonwapening + injectie scheurvorming dragende vloerplaten',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 84_000,
    },
    {
      id: 'tr-05',
      cluster: 'Ruwbouw',
      hoofdgroep: 'Ruwbouw',
      subgroep: 'Nieuwe kern',
      libraryRef: 'DEMO-NL-RW-KERN-07',
      variantLabel: 'Lift + trappenhuis',
      discipline: 'Ruwbouw',
      omschrijving: 'Nieuwe liftkern en trappenhaus in bestaande schacht (beton staal)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 412_000,
    },
    {
      id: 'tr-06',
      cluster: 'Ruwbouw',
      hoofdgroep: 'Ruwbouw',
      subgroep: 'Gevel',
      libraryRef: 'DEMO-NL-RW-GVL-12',
      variantLabel: 'Partial curtain wall',
      discipline: 'Ruwbouw',
      omschrijving: 'Gevelaanpassing nieuwe kozijnen woningen + bruglasdichting',
      eenheid: 'm²',
      aantal: 420,
      basisSubtotaal: 186_000,
    },
    {
      id: 'tr-07',
      cluster: 'Ruwbouw',
      hoofdgroep: 'Ruwbouw',
      subgroep: 'Dak',
      libraryRef: 'DEMO-NL-RW-DAK-04',
      variantLabel: 'Omgebouw naar daktuinen',
      discipline: 'Ruwbouw',
      omschrijving: 'Dakisolatie verhoogd + opstanden + drainage en ophoogzand (model)',
      eenheid: 'm²',
      aantal: 890,
      basisSubtotaal: 142_000,
    },
    {
      id: 'tr-08',
      cluster: 'Units',
      hoofdgroep: 'Woningbouw',
      subgroep: 'Casco woning',
      libraryRef: 'DEMO-NL-WB-CAS-14U',
      variantLabel: '14 units standaard',
      discipline: 'Ruwbouw',
      omschrijving: 'Casco woningen binnen schil: wanden, vloeren ruw, leidingkokers verticaal',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 1_180_000,
    },
    {
      id: 'tr-09',
      cluster: 'Units',
      hoofdgroep: 'Installaties',
      subgroep: 'Risers',
      libraryRef: 'DEMO-NL-WT-RS-09',
      variantLabel: 'WTB + E hoofdleidingen',
      discipline: 'WTB',
      omschrijving: 'Verticale hoofdleidingen warm/koud/riool + elektrische hoofdrails per kern',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 198_000,
    },
    {
      id: 'tr-10',
      cluster: 'Units',
      hoofdgroep: 'Installaties',
      subgroep: 'Warmte',
      libraryRef: 'DEMO-NL-WT-WP-04',
      variantLabel: 'Collectief warmtepompveld',
      discipline: 'WTB',
      omschrijving: 'Bronboringen + verdeelstation + leiding naar gebouw (model)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 268_000,
    },
    {
      id: 'tr-11',
      cluster: 'Algemeen',
      hoofdgroep: 'Algemene kosten',
      subgroep: 'Engineering',
      libraryRef: 'DEMO-NL-AK-ENG-TR-01',
      variantLabel: 'Utiliteit → woning',
      discipline: 'Organisatie',
      omschrijving: 'Constructieve en installatietekeningen, BIM-coördinatie en revisies',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 124_000,
    },
    {
      id: 'tr-12',
      cluster: 'Algemeen',
      hoofdgroep: 'Algemene kosten',
      subgroep: 'Bouwplaats',
      libraryRef: 'DEMO-NL-AK-BP-TR-02',
      variantLabel: 'Langdurig',
      discipline: 'Organisatie',
      omschrijving: 'Bouwplaatsinrichting, hijswerk, tijdelijke voorzieningen en beveiliging',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 156_000,
    },
    {
      id: 'tr-o1',
      cluster: 'Optie',
      hoofdgroep: 'Installaties',
      subgroep: 'PV',
      libraryRef: 'DEMO-NL-E-PV-TR-03',
      variantLabel: 'Dakveld 180 kWp',
      discipline: 'E',
      omschrijving: 'PV-panelen + omvormers + aansluiting op hoofdverdeelinrichting (model)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 142_000,
      optional: true,
    },
    {
      id: 'tr-o2',
      cluster: 'Optie',
      hoofdgroep: 'Installaties',
      subgroep: 'EV',
      libraryRef: 'DEMO-NL-E-EV-02',
      variantLabel: '8 laadpunten',
      discipline: 'E',
      omschrijving: 'Laadinfrastructuur parkeerdek + load balancing (model)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 68_000,
      optional: true,
    },
    {
      id: 'tr-o3',
      cluster: 'Optie',
      hoofdgroep: 'Woningbouw',
      subgroep: 'Afwerk pakket',
      libraryRef: 'DEMO-NL-WB-AFW-UP-01',
      variantLabel: 'Upgrade vloer + keuken',
      discipline: 'Interieur',
      omschrijving: 'Pakket hogere afwerkingsgraad voor alle units (vloer PVC + keukenblok basis)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 198_000,
      optional: true,
    },
    {
      id: 'tr-o4',
      cluster: 'Optie',
      hoofdgroep: 'Ruwbouw',
      subgroep: 'Gevel extra',
      libraryRef: 'DEMO-NL-RW-GVL-OPT-01',
      variantLabel: 'Houtlook panelen',
      discipline: 'Ruwbouw',
      omschrijving: 'Esthetische gevelbekleding zone straatzijde (materiaalupgrade)',
      eenheid: 'm²',
      aantal: 180,
      basisSubtotaal: 112_000,
      optional: true,
    },
    {
      id: 'tr-o5',
      cluster: 'Optie',
      hoofdgroep: 'Algemene kosten',
      subgroep: 'Verzekering',
      libraryRef: 'DEMO-NL-AK-VER-01',
      variantLabel: 'CAR uitgebreid',
      discipline: 'Organisatie',
      omschrijving: 'Bouwverzekering uitgebreid pakket + milieudekking (model)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 42_000,
      optional: true,
    },
    {
      id: 'tr-o6',
      cluster: 'Optie',
      hoofdgroep: 'Sloopwerk',
      subgroep: 'Asbest',
      libraryRef: 'DEMO-NL-SL-ASB-01',
      variantLabel: 'Verwijdering pakket',
      discipline: 'Sloop',
      omschrijving: 'Asbestverwijdering plafondplaten kantoorzone (indien aangetroffen — model)',
      eenheid: 'post',
      aantal: 1,
      basisSubtotaal: 86_000,
      optional: true,
    },
  ],
};

export function demoLineUnitPrice(line: DemoLine): number {
  if (line.aantal <= 0) return line.basisSubtotaal;
  return Math.round(line.basisSubtotaal / line.aantal);
}

export function scopeLabel(scope: DemoScope): string {
  return ['Compact', 'Standaard', 'Ruim'][scope];
}

export function scopeMultiplier(scope: DemoScope): number {
  return [0.9, 1, 1.12][scope];
}

export function defaultActiveLineIds(scenario: DemoScenarioId): Set<string> {
  const set = new Set<string>();
  for (const line of SCENARIO_LINES[scenario]) {
    if (!line.optional) set.add(line.id);
  }
  return set;
}

export function lineIsActive(line: DemoLine, active: Set<string>): boolean {
  if (!line.optional) return true;
  return active.has(line.id);
}

export function computeActiveSubtotaal(
  scenario: DemoScenarioId,
  scope: DemoScope,
  active: Set<string>
): number {
  const mult = scopeMultiplier(scope);
  let sum = 0;
  for (const line of SCENARIO_LINES[scenario]) {
    if (!lineIsActive(line, active)) continue;
    sum += line.basisSubtotaal * mult;
  }
  return Math.round(sum);
}

/** Verdeelt totaal in materialen / arbeid / materieel (modelverhouding, afgerond). */
export function splitCostBuckets(total: number): { materials: number; labor: number; equipment: number } {
  const materials = Math.round(total * 0.56);
  const labor = Math.round(total * 0.35);
  const equipment = Math.max(0, total - materials - labor);
  return { materials, labor, equipment };
}

export function klanttypeLabel(k: DemoKlanttype): string {
  if (k === 'particulier') return 'Particulier';
  if (k === 'zakelijk') return 'Zakelijke opdrachtgever';
  return 'Woningcorporatie';
}

export function demoRegionLabel(scenario: DemoScenarioId): string {
  if (scenario === 'renovatie') return 'Midden-Nederland';
  if (scenario === 'splitsing') return 'Randstad';
  return 'Zuidoost-Brabant';
}

export function resultaatNarrative(
  scenario: DemoScenarioId,
  klant: DemoKlanttype,
  lineCount: number,
  region: string
): string {
  const tone =
    klant === 'particulier'
      ? 'Hier zie je een heldere samenvatting van je modelbegroting.'
      : klant === 'zakelijk'
        ? 'Onderbouwing per cluster — geschikt om intern te reviewen vóór definitieve offerte.'
        : 'Transparante opbouw voor bestuur en toezicht — indicatief, geen vervanging voor aanbesteding.';
  const scopeHint =
    scenario === 'transformatie'
      ? ' Schaal en clusters zijn in dit scenario groter — in de app spiegelen je posten exact de library.'
      : '';
  const detailHint =
    ' In de echte app komt elke regel uit je prijsbibliotheek (hoofdgroep → subgroep → systeem + variant).';

  return `${tone} Dit voorbeeld bevat ${lineCount} actieve begrotingsregel${lineCount === 1 ? '' : 'en'} voor regio “${region}”. Bedragen zijn exclusief btw en illustratief.${scopeHint}${detailHint}`;
}

/** v2: uitgebreide library-regels — oude sessionStorage-sleutel genegeerd. */
export const STORAGE_KEY = 'calqtool_product_demo_v2';

export interface DemoPersistedState {
  scenario: DemoScenarioId;
  scope: DemoScope;
  klant: DemoKlanttype;
  activeIds: string[];
  rulesSaved: boolean;
  tab: 'gegevens' | 'posten' | 'resultaat' | 'rapportage';
  drawingChecked: boolean;
  docLibraryChecked: boolean;
}

export function loadDemoState(): Partial<DemoPersistedState> | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<DemoPersistedState>;
  } catch {
    return null;
  }
}

export function saveDemoState(state: DemoPersistedState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* private mode */
  }
}

export function lineMatchesFilter(line: DemoLine, q: string): boolean {
  if (!q) return true;
  const hay = [
    line.cluster,
    line.hoofdgroep,
    line.subgroep,
    line.libraryRef,
    line.variantLabel,
    line.discipline,
    line.omschrijving,
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}
